import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ClientAdminService } from './client-admin.service';
import { ClientSaveModalComponent } from '../client-save-modal/client-save-modal.component';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-client',
    templateUrl: './client.component.html',
    styleUrls: ['./client.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ClientComponent implements OnInit, OnDestroy {
  clientList: any[] = [];
  searchClient = '';
  loading = false;
  refreshMoment = '';
  currentClientKey = '';

  showConfirmDelete = false;
  pendingDeleteClient: any = null;
  confirmDeleteConfig: ConfirmationModalConfig = {
    title: 'Remove Client',
    message: '',
    iconClass: 'fa fa-exclamation-triangle',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };

  private destroy$ = new Subject<void>();

  constructor(
    private appService: AppService,
    private clientAdminService: ClientAdminService,
    private gtwSnackbarService: GtwSnackbarService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const userSettings = this.appService.USER_SETTINGS;
    if (userSettings?.user) {
      this.currentClientKey = String(userSettings.user.client_key ?? '');
    }
    this.loadAllClients();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredClientList(): any[] {
    const term = (this.searchClient || '').toLowerCase().trim();
    if (!term) return this.clientList;
    return this.clientList.filter((c: any) =>
      (c.client_name || '').toLowerCase().includes(term) ||
      (c.client_desc || '').toLowerCase().includes(term) ||
      String(c.client_key ?? '').includes(term)
    );
  }

  onSearchChange(value: string): void {
    this.searchClient = value;
    this.cdr.detectChanges();
  }

  loadAllClients(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.clientAdminService.getAllClients()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.loading = false;
          const result = data?.clientResult;
          if (result && !result.errorMsg) {
            this.clientList = result.clientList || [];
            this.clientList.forEach((item: any) => this.loadClientLogo(item));
          } else {
            this.gtwSnackbarService.openSnackBar('Problem getting clients.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Problem getting clients.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  refresh(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.clientAdminService.getAllClients()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.loading = false;
          const result = data?.clientResult;
          if (result && !result.errorMsg) {
            this.clientList = result.clientList || [];
            this.clientList.forEach((item: any) => this.loadClientLogo(item));
            this.refreshMoment = new Date().toLocaleTimeString();
          } else {
            this.gtwSnackbarService.openSnackBar('Problem getting clients.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Problem getting clients.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  addNewClient(): void {
    this.openSaveModal({});
  }

  editClient(client: any): void {
    this.openSaveModal(client);
  }

  toggleClientActive(client: any, event: any): void {
    const previousStatus = client.client_active;
    const newStatus = event?.checkboxState === true ? '1' : '0';
    if (newStatus === previousStatus) {
      return;
    }
    // Optimistically apply the new status so the toggle reflects the click.
    client.client_active = newStatus;
    this.clientAdminService.toggleClientActiveStatus(client.client_key, newStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (String(data?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar(data.successMessage || 'Client status updated.', 'gtw-snackbar--green');
          } else {
            // 200 + callSuccess=0: surface the API message and roll back.
            this.revertClientToggle(client, previousStatus, data?.errorMessage || 'Problem toggling client active status.');
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          // HTTP error code (e.g. no access to client resources): surface the
          // API message and roll back the optimistic toggle.
          this.revertClientToggle(client, previousStatus, this.extractApiErrorMessage(err, 'Problem toggling client active status.'));
          this.cdr.detectChanges();
        },
      });
  }

  private revertClientToggle(client: any, previousStatus: any, message: string): void {
    client.client_active = previousStatus;
    this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--red');
  }

  /**
   * Extracts a human-readable message from an HttpErrorResponse: a raw string
   * body, a `{ errorMessage | error | message }` object, or the error itself.
   */
  private extractApiErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      const fromBody = body.errorMessage || body.error || body.message;
      if (fromBody) {
        return fromBody;
      }
    }
    return err?.errorMessage || err?.message || fallback;
  }

  confirmRemoveClient(client: any): void {
    this.pendingDeleteClient = client;
    this.confirmDeleteConfig = {
      ...this.confirmDeleteConfig,
      message: `Are you sure you want to remove this client ${client.client_name}?`,
    };
    this.showConfirmDelete = true;
    this.cdr.detectChanges();
  }

  onConfirmDeleteYes(): void {
    this.showConfirmDelete = false;
    if (!this.pendingDeleteClient) return;
    const clientKey = this.pendingDeleteClient.client_key;
    this.pendingDeleteClient = null;

    this.clientAdminService.deleteClient(clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (String(data?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Client removed successfully.', 'gtw-snackbar--green');
            this.loadAllClients();
          } else {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Problem removing client.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Problem removing client.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  onConfirmDeleteNo(): void {
    this.showConfirmDelete = false;
    this.pendingDeleteClient = null;
    this.cdr.detectChanges();
  }

  push(): void {
    this.clientAdminService.push()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.gtwSnackbarService.openSnackBar('Push initiated successfully.', 'gtw-snackbar--green');
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Problem with push.', 'gtw-snackbar--red');
        },
      });
  }

  isLoggedInClient(clientKey: string): boolean {
    return String(clientKey) === this.currentClientKey;
  }

  trackByClientKey(_: number, client: any): string {
    return String(client.client_key ?? _);
  }

  private openSaveModal(rowData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '80vw';
    config.maxWidth = '80vw';
    config.disableClose = true;
    config.data = { rowData };

    const ref = this.dialog.open(ClientSaveModalComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result === 'saved') {
          this.loadAllClients();
        }
      });
  }

  private loadClientLogo(client: any): void {
    if (client?.client_key == null || client.client_key === '') return;
    this.clientAdminService.downloadClientLogo(client.client_key)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          if (blob && blob.size > 0) {
            const url = URL.createObjectURL(new Blob([blob], { type: 'image/jpeg' }));
            client.logo_url = this.sanitizer.bypassSecurityTrustUrl(url);
            this.cdr.detectChanges();
          }
        },
        error: () => {},
      });
  }
}
